import React, { useContext, useEffect, useReducer, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import FormControl from "react-bootstrap/FormControl";
import InputGroup from "react-bootstrap/InputGroup";
import { Helmet } from "react-helmet-async";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import "../ProductListScreen.css";
import { Store } from "../Store";
import DeleteModal from "../components/DeleteModal";
import LoadingBox from "../components/LoadingBox";
import MenuIconAI from "../components/MenuIconAI";
import { default as config } from "../config.json";
import useAxios from "../hooks/useAxios";
import useProductsAndCategories from "../hooks/useProductsAndCategories";
import { getError, turkishCurrencyFormatterNoPrecision } from "../utils";
import ProductEditScreen from "./ProductEditScreen";

const reducer = (state, action) => {
  switch (action.type) {
    case "DELETE_REQUEST":
      return { ...state, loadingDelete: true, successDelete: false };
    case "DELETE_SUCCESS":
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case "DELETE_FAIL":
      return { ...state, loadingDelete: false, successDelete: false };

    case "DELETE_RESET":
      return { ...state, loadingDelete: false, successDelete: false };
    default:
      return state;
  }
};

export default function ProductListScreen() {
  const axios = useAxios();
  const [{ loadingDelete }, dispatch] = useReducer(reducer, {
    loadingDelete: false,
  });

  const { state } = useContext(Store);
  const { products } = state;
  const [type, setType] = useState("all");
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductEditor, setShowProductEditor] = useState(false);
  const [query, setQuery] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const [, , setLoadProducts, setLoadCategories] = useProductsAndCategories();

  const deleteHandler = async (product) => {
    try {
      dispatch({ type: "DELETE_REQUEST" });

      if (product.image !== config.default_image)
        try {
          await axios.delete(`/api/upload/${product._id}?entityName=product`);
        } catch (error) {}

      await axios.delete(`/api/products/${product._id}`);
      setLoadCategories(true);
      setLoadProducts(true);
      toast.success("Ürün başarılı bir şekilde silindi");
      dispatch({ type: "DELETE_SUCCESS" });
    } catch (err) {
      toast.error(getError(err));
      dispatch({
        type: "DELETE_FAIL",
      });
    }
  };

  const handleClickNewProduct = () => {
    setSelectedProduct(null);
    setShowProductEditor(true);
  };

  const confirmDeleteHandler = (product) => {
    setProductToDelete(product);
    setShowConfirmModal(true);
  };

  useEffect(() => {
    const productsFiltered = products
      ?.filter((p) =>
        query
          ? p.name.toLocaleLowerCase().includes(query.toLocaleLowerCase())
          : true
      )
      .filter((p) =>
        type === "customerFirstOrder" ? p.isCustomerFirstOrder : true
      );

    setFilteredProducts(productsFiltered);
  }, [query, products, type]);

  return (
    <div className="product-grid-container">
      <Helmet>
        <title>Ürünler</title>
      </Helmet>
      {!isTabletOrMobile && (
        <>
          <h1 className="my-3 text-muted">Ürünler</h1>
          <hr className="hr"></hr>
        </>
      )}
      <div className="product-grid-toolbar">
        <div>
          <InputGroup className="mb-3">
            <FormControl
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Urun ara..."
              aria-label="Urun ara"
              aria-describedby="button-search"
            ></FormControl>
            <Button variant="outline-primary" onClick={() => setQuery("")}>
              Temizle
            </Button>
          </InputGroup>
          <InputGroup className="mb-3">
            <Button
              className={
                type === "all"
                  ? "text-primary border-primary"
                  : "text-secondary border-secondary"
              }
              variant="light"
              onClick={() => setType("all")}
            >
              Hepsi
            </Button>
            &nbsp;
            <Button
              className={
                type === "customerFirstOrder"
                  ? "text-primary border-primary"
                  : "text-secondary border-secondary"
              }
              variant="light"
              onClick={() => setType("customerFirstOrder")}
            >
              Karekod Anasayfa
            </Button>
          </InputGroup>
        </div>
        <div className="ms-auto">
          <Button
            type="button"
            onClick={handleClickNewProduct}
            className={"text-primary border-primary"}
            variant="light"
          >
            <i className="fas fa-plus"></i>
            {!isTabletOrMobile && <>&nbsp; Ürün Oluştur</>}
          </Button>
        </div>
      </div>

      <div className="product-grid">
        {filteredProducts && filteredProducts.length > 0 ? (
          filteredProducts.map((product) => (
            <div className="product-card" key={product._id}>
              <img
                src={product.image || config.default_image}
                alt={product.name}
                className="product-image"
                onClick={() => {
                  setSelectedProduct(product);
                  setShowProductEditor(true);
                }}
              />
              {product.questions?.length > 0 && <MenuIconAI />}
              <h5 className="product-name">{product.name}</h5>
              <p className="product-price">
                {turkishCurrencyFormatterNoPrecision(product.price)}
              </p>
              {loadingDelete && productToDelete?._id === product._id ? (
                <LoadingBox />
              ) : (
                <Button
                  onClick={() => confirmDeleteHandler(product)}
                  className="delete-button"
                >
                  Sil
                </Button>
              )}
            </div>
          ))
        ) : (
          <p>Sisteme kayıtlı ürün bulunamadı</p>
        )}
      </div>

      <Modal
        backdrop="static"
        show={showProductEditor}
        onHide={() => {
          setShowProductEditor(false);
        }}
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>
            <span className="text-muted">
              {selectedProduct === "0" ? "Yeni Ürün" : selectedProduct?.name}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ProductEditScreen
            isTabletOrMobile={isTabletOrMobile}
            product={
              selectedProduct || { expenseSubType: "DIGER", unit: "ADET-1" }
            }
            setShowProductEditor={setShowProductEditor}
          />
        </Modal.Body>
      </Modal>

      <DeleteModal
        showConfirmModal={showConfirmModal}
        setShowConfirmModal={setShowConfirmModal}
        deleteHandler={deleteHandler}
        objectToDelete={productToDelete}
        title={"Ürünü Sil"}
        message={"Silmek istediğinize emin misiniz?"}
      />
    </div>
  );
}
